import {
  S3Client,
  GetObjectCommand,
  HeadObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
} from '@aws-sdk/client-s3';
import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'src/environments/environment';

const REGION = 'eu-west-3';
const BUCKET = environment.bucket;

async function bodyToText(body: any): Promise<string> {
  if (typeof body?.transformToString === 'function') {
    return body.transformToString();
  }

  const uint8 = await bodyToUint8Array(body);
  return new TextDecoder('utf-8').decode(uint8);
}

async function bodyToUint8Array(body: any): Promise<Uint8Array> {
  if (typeof body?.transformToByteArray === 'function') {
    const bytes = await body.transformToByteArray();
    return new Uint8Array(bytes);
  }

  if (body instanceof ReadableStream) {
    const reader = body.getReader();
    const chunks: Uint8Array[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(value);
    }

    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const merged = new Uint8Array(totalLength);

    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    return merged;
  }

  throw new Error('Unsupported body type');
}

@Injectable({
  providedIn: 'root',
})
export class S3Service {
  public async validateUpload(file: File, kind: 'image' | 'pdf'): Promise<void> {
    if (!file || file.size === 0) {
      throw new Error('Le fichier sélectionné est vide.');
    }

    const maxSize = kind === 'pdf' ? 20 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error(kind === 'pdf'
        ? 'La plaquette ne doit pas dépasser 20 Mo.'
        : 'Le logo ne doit pas dépasser 5 Mo.');
    }

    const header = new Uint8Array(await file.slice(0, 512).arrayBuffer());
    if (kind === 'pdf') {
      const isPdf = header.length >= 5 && String.fromCharCode(...header.slice(0, 5)) === '%PDF-';
      if (!isPdf) throw new Error('Le fichier sélectionné n’est pas un PDF valide.');
      return;
    }

    const isPng = header.length >= 8 &&
      header[0] === 0x89 && header[1] === 0x50 && header[2] === 0x4e && header[3] === 0x47;
    const isJpeg = header.length >= 3 &&
      header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;

    // SVG is refused on purpose: assets are served from the admin domain and an
    // SVG can embed scripts able to read the admin session.
    if (!isPng && !isJpeg) {
      throw new Error('Le logo doit être une image PNG ou JPEG valide.');
    }
  }

  // Assets are served from the admin domain: refuse any format the browser
  // could execute there (SVG, HTML, XML, JavaScript), whatever the caller.
  public isExecutableContent(key: string, contentType?: string): boolean {
    const type = (contentType || '').toLowerCase().split(';')[0].trim();
    const executableTypes = [
      'image/svg+xml', 'text/html', 'application/xhtml+xml', 'text/xml',
      'application/xml', 'text/javascript', 'application/javascript',
    ];
    return executableTypes.includes(type) ||
      /\.(svgz?|html?|xhtml|xml|m?js)$/i.test((key || '').trim());
  }

  public buildAssetKey(scope: string, fileName: string): string {
    const safeScope = (scope || 'misc')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9/_-]+/g, '-')
      .replace(/^\/+|\/+$/g, '');
    const extensionMatch = (fileName || '').match(/(\.[a-zA-Z0-9]+)$/);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
    const baseName = (fileName || 'fichier')
      .slice(0, extension ? -extension.length : undefined)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'fichier';
    const uniqueId = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

    return `assets-upload/${safeScope}/${uniqueId}-${baseName}${extension}`;
  }

  public isValidObjectKey(key: string | null | undefined): key is string {
    const normalized = (key || '').trim();
    return Boolean(
      normalized &&
      normalized !== 'null' &&
      normalized !== 'undefined' &&
      !normalized.endsWith('/null') &&
      !normalized.endsWith('/undefined')
    );
  }

  private async createClient(): Promise<S3Client> {
    const session = await fetchAuthSession({ forceRefresh: true });

    if (!session.credentials) {
      throw new Error('Aucun credential AWS disponible dans la session Amplify');
    }

    return new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });
  }

  async getTextObject(key: string): Promise<string> {
    const client = await this.createClient();

    const response = await client.send(
      new GetObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    return bodyToText(response.Body);
  }

  async getObject(key: string): Promise<{
    body: Uint8Array;
    contentType?: string;
    contentLength?: number;
    lastModified?: Date;
    etag?: string;
  }> {
    const client = await this.createClient();

    const response = await client.send(
      new GetObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    const body = await bodyToUint8Array(response.Body);

    return {
      body,
      contentType: response.ContentType,
      contentLength: response.ContentLength,
      lastModified: response.LastModified,
      etag: response.ETag,
    };
  }

  async getObjectAsBlob(key: string): Promise<Blob> {
    const result = await this.getObject(key);

    const uint8Array = new Uint8Array(result.body);

    return new Blob([uint8Array], {
      type: result.contentType || 'application/octet-stream',
    });
  }

  async getObjectUrl(key: string): Promise<string> {
    const blob = await this.getObjectAsBlob(key);
    return URL.createObjectURL(blob);
  }

  async putFile(params: {
    key: string;
    body: Blob | File | Uint8Array | string;
    contentType?: string;
  }): Promise<boolean> {
    if (!this.isValidObjectKey(params.key)) {
      throw new Error('Clé S3 invalide');
    }

    if (params.body === null || params.body === undefined) {
      throw new Error('Le fichier à téléverser est vide');
    }

    if (this.isExecutableContent(params.key, params.contentType)) {
      throw new Error('Ce format de fichier n’est pas autorisé.');
    }

    const client = await this.createClient();
    // Recent AWS SDK versions may route browser File/Blob instances through
    // their streaming signer, which is not compatible with Angular's patched
    // stream implementation. A Uint8Array has a known length and is uploaded
    // through the reliable non-streaming path instead.
    const body = params.body instanceof Blob
      ? new Uint8Array(await params.body.arrayBuffer())
      : params.body;

    await client.send(
      new PutObjectCommand({
        Bucket: BUCKET,
        Key: params.key,
        Body: body,
        ContentType: params.contentType,
      })
    );

    await client.send(
      new HeadObjectCommand({
        Bucket: BUCKET,
        Key: params.key,
      })
    );

    return true;
  }

  async deleteObject(key: string): Promise<boolean> {
    if (!this.isValidObjectKey(key)) {
      return false;
    }

    const client = await this.createClient();

    await client.send(
      new DeleteObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    return true;
  }
}
